/**
 * Enquête n°28 — Angle mort et 2-roues : la zone qui tue.
 * Article premium hebdomadaire.
 */

import { Lightbulb, ShieldCheck, AlertTriangle, Quote, Users } from 'lucide-react';

export const angleMort2RouesEnquete = {
  slug: 'angle-mort-2-roues',
  themeCode: 'AU',
  issueNumber: 28,
  totalIssues: 31,
  series: 'enquetes',
  category: 'Enquête sécurité routière',
  shortTitle: 'Angle mort',
  title: "L'angle mort à moto : la zone qui tue (et personne n'en parle assez)",
  kicker: 'Enquête n°28 · Série « Témoignages & Sécurité »',
  subtitle:
    "38 % des accidents corporels en agglomération impliquant un 2-roues motorisé surviennent à un carrefour ou lors d'un changement de voie. La cause n°1 ? L'angle mort. Comprends où il se trouve, pourquoi tu n'existes pas pour l'automobiliste, et les 5 réflexes qui te sauvent la vie.",
  heroImage: '/img/magazines/autres-usagers.jpg',
  heroImageAlt: 'Voiture changeant de voie sans voir une moto dans son angle mort',
  readTimeMin: 8,
  seoDescription:
    "Enquête complète sur l'angle mort à moto : zones invisibles d'une voiture, d'un camion, statistiques ONISR, MAIDS. 5 réflexes pour ne plus jamais y disparaître.",
  seoKeywords:
    'angle mort moto, accident changement de voie, MAIDS, ONISR, visibilité motard, zone aveugle camion, 2-roues sécurité',

  edito: {
    title: "Tu n'es pas vu. Donc tu n'existes pas.",
    body: "Dans 70 % des accidents impliquant une voiture et une moto, l'automobiliste déclare : « <strong>Je ne l'ai pas vu</strong> ». Ce n'est pas (toujours) un mensonge. C'est de la physique de la perception : l'œil humain en mouvement, les piliers de carrosserie, le rétroviseur mal réglé, la moto fine de face. <strong>L'angle mort, ce n'est pas une fatalité — c'est une zone géographique précise.</strong> Et tu peux apprendre à en sortir. Cette enquête te dit où.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: "Où se trouvent EXACTEMENT les angles morts d'une voiture ?",
      paragraphs: [
        "Une berline a <strong>4 angles morts majeurs</strong>. À l'avant : derrière les piliers A (entre pare-brise et porte). Sur les côtés : à hauteur des portières arrière, entre le rétroviseur extérieur et le champ de vision périphérique du conducteur. À l'arrière : sous la lunette, jusqu'à environ 3 m derrière le pare-chocs.",
        "<strong>Règle des rétroviseurs</strong> : un rétro extérieur bien réglé couvre une bande latérale de ~3 m. Au-delà, c'est l'angle mort. Une moto qui dépasse à droite ou à gauche TRAVERSE forcément cette zone — pendant environ 2 à 4 secondes selon la vitesse différentielle.",
        "<strong>Pilier A : le tueur silencieux</strong>. Il mesure 8 à 15 cm de large et masque une zone qui croît avec la distance. À 30 m, le pilier A masque une moto entière qui s'approche d'un carrefour. C'est la cause majeure des refus de priorité tournant à gauche.",
      ],
      keyPoints: [
        '4 angles morts par voiture',
        'Pilier A masque une moto à 30 m',
        'Côté : 2-4 s de traversée',
        'Arrière : 3 m sous lunette',
      ],
    },
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 2',
      title: 'Le cas du camion : 6 angles morts, dont 1 mortel',
      paragraphs: [
        "Un poids lourd (PL) a <strong>6 angles morts</strong> : avant (jusqu'à 3 m devant la cabine), côté droit (jusqu'à 2,5 m sur toute la longueur, le pire), côté gauche (1 m), arrière (jusqu'à 30 m sans rétro caméra), au-dessus de la calandre, et SOUS le PL en cas de chute.",
        "<strong>Le côté droit du camion</strong> est responsable d'environ 30 % des accidents mortels piéton-cycliste-2roues en ville. Pourquoi ? Parce que le motard se faufile en interfile à droite, et le PL tourne à droite sans le voir — la moto passe sous les roues.",
        "<strong>Règle d'or</strong> : ne JAMAIS se mettre à droite d'un camion à un carrefour. Si tu es bloqué, recule, ou attends derrière. Si le PL n'a pas son clignotant droit, considère qu'il peut quand même tourner — beaucoup de chauffeurs oublient.",
      ],
      keyPoints: [
        '6 angles morts sur un PL',
        'Côté droit = zone mortelle',
        "Jamais à droite d'un PL en carrefour",
        'Clignotant droit absent ≠ pas de virage',
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: "La physique de l'inattention sélective",
      body: "Les neurosciences l'ont démontré (Simons & Chabris, 1999) : un cerveau humain ne peut consciemment traiter que ~3 objets en mouvement simultanément. <strong>Quand l'automobiliste cherche une voiture, il NE VOIT PAS une moto même en plein champ de vision</strong>. Ce biais s'appelle l'inattention sélective. La moto est <strong>statistiquement absente</strong> du paysage attendu : il y a 1 moto pour 50 voitures sur les routes françaises. Le cerveau filtre ce qui est rare. Tu n'es pas invisible. Tu es <strong>imprévu</strong>.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'Les 5 réflexes pour ne plus disparaître',
      paragraphs: [
        "<strong>1. Rouler dans le rétroviseur</strong> : si tu vois le rétro extérieur du véhicule, le conducteur peut te voir. Si tu ne vois PAS le rétro, tu es dans l'angle mort. Sors-en immédiatement (accélère ou ralentis).",
        "<strong>2. Position de roulage active</strong> : sur file unique, roule à 1/3 gauche de ta voie (pas au milieu). Tu apparais plus tôt dans le champ de vision du conducteur, et tu te démarques du flux camion-voiture.",
        "<strong>3. Éclairage croisé en permanence</strong> : feux de croisement OBLIGATOIRES de jour depuis 1975 (R313-3). Mais beaucoup oublient un feu HS. Vérifie chaque semaine. Un casque clair augmente la visibilité de 19 % (étude Wells, BMJ).",
        "<strong>4. Anticipe les intentions</strong> : roues avant qui tournent légèrement, clignotant qui clignote depuis 2 secondes, tête qui se penche sur le rétro — autant de signaux. Réduis et écarte-toi avant même que l'autre n'agisse.",
        "<strong>5. Avertisseur — usage assumé</strong> : 1 coup bref devant un véhicule qui s'apprête à changer de voie n'est ni grossier ni inutile. C'est de l'auto-protection. Tu existes : fais-le entendre.",
      ],
      keyPoints: [
        'Voir le rétro = être visible',
        'Rouler 1/3 gauche de voie',
        'Feux + casque clair',
        'Anticiper les intentions',
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: "Le piège mortel : l'interfile à droite",
      body: "L'interfile (circulation entre voies) est <strong>autorisée dans 21 départements</strong> en expérimentation depuis août 2021 (étendue jusqu'à 2025). MAIS : elle doit se faire entre la voie de gauche et la voie centrale (sur autoroute 2x3 voies). <strong>L'interfile à droite reste interdite et mortelle</strong> (zone d'angle mort + cycliste/piéton imprévu + voitures qui changent de voie pour sortir). Vérifie ton département et respecte STRICTEMENT le côté gauche.",
    },
    {
      type: 'tip',
      icon: Users,
      title: 'À retenir avant de remonter en selle',
      body: "L'angle mort n'est pas une malédiction. C'est une zone <strong>connue, mesurable, évitable</strong>. La règle ultime : <strong>si tu n'es pas sûr que l'autre t'a vu, considère qu'il ne t'a pas vu</strong>. Tu rentres chez toi. C'est tout ce qui compte.",
    },
  ],

  // QCM contextuel : 8 questions du thème « Autres Usagers » pour valider la compréhension
  quiz: {
    themeCode: 'U',
    questionsCount: 8,
    passingScore: 6,
    title: 'QCM : ai-je bien compris l\'angle mort ?',
    description: '<strong>8 questions</strong> du thème « Autres Usagers » pour tester ta compréhension. Objectif : <strong>6/8</strong> pour valider.',
  },
};

export default angleMort2RouesEnquete;
